<script setup>
import { useVModel } from "@vueuse/core/index";
import { isEqual, uniqBy } from "lodash";
import { computed, onMounted, ref, toRefs, unref, watch } from "vue";
import TreeSelect from "@/components/shared/TreeSelect";
import { MAX_FACET_HITS } from "../../constants.js";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  filterName: {
    type: String,
  },
  algoliaConfiguration: {
    type: Object,
  },
  placeholder: {
    type: String,
    required: false,
  },
  currentSearchIndex: {
    type: Object,
    required: true,
  },
  showAll: {
    type: Boolean,
    required: false,
    default: false,
  },
  labelledBy: {
    type: String,
    required: false,
    default: "treeselect",
  },
});

const emit = defineEmits(["update:modelValue"]);
const filters = useVModel(props, "modelValue", emit);
const { algoliaConfiguration, currentSearchIndex, showAll } = toRefs(props);

const treeOptions = ref([]);
const selectedNodes = ref([]);
const refinementListRef = ref(undefined);
const initialFacets = ref([]);

const facetName = computed(() => {
  return unref(algoliaConfiguration)?.[props?.filterName];
});

const itemTransform = (items, { results }) => {
  // If Algolia doesn't return enough results, we lose track of a
  // potentially refined facet.
  // For example, if you refine on "Apple", then search for "chromecast",
  // "Apple" is no longer returned, and we don't know that it was selected
  // based on the initial facets.
  // We need to keep track of the last state to reflect the fact that it
  // was refined in the UI.
  initialFacets.value.forEach((facet, index) => {
    const updatedItem = items.find((item) => item.value === facet.value);

    if (updatedItem) {
      initialFacets.value[index] = {
        ...updatedItem,
        count: 0,
      };
    }
  });

  // If a cached facet is already returned by Algolia, we want it to be
  // displayed rather than to display its cached value.
  // You might need to sort the items again here because the internal
  // sorting happens before `transformItems` is called.
  const updatedItems = uniqBy([...items, ...initialFacets.value], "value").slice(0, 1000);

  const rootNode = updatedItems?.reduce((acc, item) => {
    const [parent, child] = item.value?.split(" > ") ?? [undefined, undefined];
    if (!acc?.[parent]) {
      acc[parent] = {
        key: parent,
        label: parent,
        children: [],
      };
    }
    acc[parent].children.push({
      key: item?.value,
      label: child,
      children: [],
    });
    return acc;
  }, {});
  treeOptions.value = Object.values(rootNode);
  return items;
};

const selectOptions = (options, refine) => {
  const childOptions = options.reduce((acc, val) => {
    const parts = val?.key?.split(" > ");
    if (parts?.length === 2) {
      acc.push(val.key);
    }
    return acc;
  }, []);

  const updatedFilters = {};
  updatedFilters[props.filterName] = childOptions;
  filters.value = { ...filters.value, ...updatedFilters };
};

watch(
  () => refinementListRef?.value?.state?.canRefine,
  (newCanRefine, oldCanRefine) => {
    if (newCanRefine) {
      const algoliaState = unref(refinementListRef)?.state;
      const updatedFilterValue = filters.value?.[props.filterName];
      if (algoliaState?.canRefine && algoliaState?.items) {
        for (const item of algoliaState.items) {
          const foundEntry = updatedFilterValue?.find((element) => element === item?.value);
          if ((foundEntry && !item.isRefined) || (!foundEntry && item.isRefined)) {
            unref(refinementListRef).refine(item.value);
          }
        }
      }
      selectedNodes.value = filters.value?.[props.filterName].map((val) => ({ key: val, checked: true }));
    }
  },
);

watch(
  () => filters.value,
  (updatedFilter, oldFilter) => {
    if (!isEqual(updatedFilter?.[props.filterName], oldFilter?.[props.filterName])) {
      const algoliaState = unref(refinementListRef)?.state;
      const updatedFilterValue = updatedFilter?.[props.filterName] ?? [];
      if (algoliaState?.canRefine && algoliaState?.items) {
        for (const item of algoliaState.items) {
          const foundEntry = updatedFilterValue?.find((element) => element === item?.value);
          if ((foundEntry && !item.isRefined) || (!foundEntry && item.isRefined)) {
            unref(refinementListRef).refine(item.value);
          }
        }
        if (showAll.value) {
          for (const filterValue of updatedFilterValue) {
            const foundEntry = algoliaState?.items?.find((element) => element?.value === filterValue);
            if (!foundEntry) {
              unref(refinementListRef).refine(filterValue);
            }
          }
        }
        selectedNodes.value = updatedFilter[props.filterName].map((val) => ({ key: val, checked: true }));
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (showAll) {
    currentSearchIndex.value
      .searchForFacetValues(facetName.value, "*", { maxFacetHits: MAX_FACET_HITS })
      .then(({ facetHits }) => {
        initialFacets.value.push(
          ...facetHits.map((facet) => ({
            ...facet,
            label: facet.value,
            value: facet.value,
            isRefined: false,
            count: 0,
          })),
        );
      })
      .catch((e) => {
        console.log(e);
      });
  }
});
</script>
<template>
  <div>
    <ais-refinement-list
      :attribute="facetName"
      :limit="1000"
      operator="or"
      :sort-by="[`name:asc`]"
      :transform-items="itemTransform"
      ref="refinementListRef"
    >
      <template v-slot="{ items, refine, searchForItems }">
        <div v-show="items?.length === 0" class="text-xs bg-gray-75 text-indigo-base rounded-md mt-1">
          <p>No options available. Clear a filter on another category.</p>
        </div>
        <div v-show="items?.length !== 0">
          <TreeSelect @update:selected-tree-nodes="(options) => selectOptions(options, refine)" :selected-tree-nodes="selectedNodes" :tree-options="treeOptions" :placeholder="placeholder" :labelled-by="labelledBy" show-chips />
        </div>
      </template>
    </ais-refinement-list>
  </div>
</template>

<style scoped></style>
